// Microphone capture → 16 kHz PCM16 frames of ~100 ms.
//
// The mic is opened RAW: echo cancellation, noise suppression and auto gain
// OFF. The server's speech gate, noise-floor tracking and noisy-room hints are
// calibrated on raw levels (as in the Unmute desktop app since 2026-07-15);
// browser DSP would move every threshold.

import { Resampler16k, TARGET_RATE } from './resample'

const FRAME_SAMPLES = TARGET_RATE / 10 // 100 ms
const WORKLET_BLOCK = 2048

const WORKLET_SOURCE = `
class UnmuteCapture extends AudioWorkletProcessor {
  constructor() {
    super(); this.buf = new Float32Array(${WORKLET_BLOCK}); this.len = 0
    this.port.onmessage = (e) => {
      if (e.data !== 'flush') return
      if (this.len) this.port.postMessage(this.buf.slice(0, this.len))
      this.len = 0
      this.port.postMessage('flushed')
    }
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0]
    if (ch) {
      let o = 0
      while (o < ch.length) {
        const n = Math.min(ch.length - o, this.buf.length - this.len)
        this.buf.set(ch.subarray(o, o + n), this.len)
        this.len += n; o += n
        if (this.len === this.buf.length) { this.port.postMessage(this.buf.slice(0)); this.len = 0 }
      }
    }
    return true
  }
}
registerProcessor('unmute-capture', UnmuteCapture)
`

export interface CaptureHandlers {
  onFrame(pcm: Int16Array): void
  onLevel(rms: number): void
}

export class MicCapture {
  private ctx: AudioContext | null = null
  private stream: MediaStream | null = null
  private node: AudioNode | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private resampler: Resampler16k | null = null
  private frame = new Int16Array(FRAME_SAMPLES)
  private frameLen = 0
  private lastLevelAt = 0
  private flushed: (() => void) | null = null

  constructor(private readonly handlers: CaptureHandlers) {}

  private shared = false

  async start(deviceId?: string): Promise<void> {
    const raw = { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1 }
    prewarmAudio() // no-op when the Dictation constructor already did it
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: deviceId ? { ...raw, deviceId: { exact: deviceId } } : raw })
    // Reuse the pre-warmed 16 kHz context: its capture processor is already
    // loaded, so audio flows the moment the mic opens and the first word
    // isn't lost. (Firefox refuses a mic on a context whose rate differs from
    // the device; there we fall back to a native-rate context + our resampler.)
    const w = warm
    if (w && w.ctx.state !== 'closed') {
      try {
        this.source = w.ctx.createMediaStreamSource(this.stream)
        this.ctx = w.ctx
        this.shared = true
      } catch { /* rate mismatch: fall through */ }
    }
    if (!this.ctx) {
      this.ctx = new (audioContextClass())({ latencyHint: 'interactive' })
      this.source = this.ctx.createMediaStreamSource(this.stream)
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume().catch(() => {})
    this.resampler = new Resampler16k(this.ctx.sampleRate)
    const workletReady = this.shared && w ? await w.worklet : await loadWorklet(this.ctx)
    this.node = this.createProcessor(this.ctx, workletReady)
    this.source!.connect(this.node)
    // Some engines only pull a node that reaches the destination; route it
    // through a muted gain so nothing is ever played back.
    const mute = this.ctx.createGain()
    mute.gain.value = 0
    this.node.connect(mute).connect(this.ctx.destination)
  }

  private createProcessor(ctx: AudioContext, workletReady: boolean): AudioNode {
    if (workletReady) {
      try {
        const node = new AudioWorkletNode(ctx, 'unmute-capture', { numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1 })
        node.port.onmessage = (e: MessageEvent<Float32Array | string>) => {
          if (typeof e.data === 'string') { this.flushed?.(); return }
          this.onSamples(e.data)
        }
        return node
      } catch { /* fall back below */ }
    }
    // CSP without blob: workers, or an engine without AudioWorklet.
    const sp = ctx.createScriptProcessor(WORKLET_BLOCK, 1, 1)
    sp.onaudioprocess = (e) => this.onSamples(new Float32Array(e.inputBuffer.getChannelData(0)))
    return sp
  }

  private onSamples(block: Float32Array): void {
    if (!this.resampler) return
    let sum = 0
    for (let i = 0; i < block.length; i++) sum += block[i] * block[i]
    const now = Date.now()
    if (now - this.lastLevelAt >= 50) {
      this.lastLevelAt = now
      this.handlers.onLevel(Math.sqrt(sum / Math.max(1, block.length)))
    }
    const pcm = this.resampler.push(block)
    let o = 0
    while (o < pcm.length) {
      const n = Math.min(pcm.length - o, FRAME_SAMPLES - this.frameLen)
      this.frame.set(pcm.subarray(o, o + n), this.frameLen)
      this.frameLen += n
      o += n
      if (this.frameLen === FRAME_SAMPLES) this.emitFrame()
    }
  }

  private emitFrame(): void {
    if (this.frameLen === 0) return
    this.handlers.onFrame(this.frame.slice(0, this.frameLen))
    this.frameLen = 0
  }

  /** Stop the mic and flush the last partial frame. */
  async stop(): Promise<void> {
    // Drain the worklet's partial block first so the last syllable is kept.
    if (isWorklet(this.node)) {
      const node = this.node
      await new Promise<void>((resolve) => {
        const t = setTimeout(resolve, 150)
        this.flushed = () => { clearTimeout(t); resolve() }
        node.port.postMessage('flush')
      })
      this.flushed = null
    }
    this.stream?.getTracks().forEach((t) => t.stop())
    this.source?.disconnect()
    this.node?.disconnect()
    if (isWorklet(this.node)) this.node.port.onmessage = null
    // The shared warm context is kept (suspended) for the next dictation.
    if (this.shared) await this.ctx?.suspend().catch(() => {})
    else await this.ctx?.close().catch(() => {})
    this.shared = false
    this.emitFrame()
    this.ctx = null
    this.stream = null
    this.node = null
    this.source = null
    this.resampler = null
  }
}

function isWorklet(n: AudioNode | null): n is AudioWorkletNode {
  return typeof AudioWorkletNode !== 'undefined' && n instanceof AudioWorkletNode
}

// ─── Pre-warming ───
// Creating the AudioContext and loading the capture worklet takes a few
// hundred ms. Done at start() it clipped the first word of the first
// dictation (measured 2026-09-19). The Dictation constructor warms both up
// ahead of time; the context stays suspended (no audio device) until start().

interface Warm { ctx: AudioContext; worklet: Promise<boolean> }
let warm: Warm | null = null

function audioContextClass(): typeof AudioContext {
  return (globalThis as any).AudioContext ?? (globalThis as any).webkitAudioContext
}

async function loadWorklet(ctx: AudioContext): Promise<boolean> {
  try {
    if (!ctx.audioWorklet) return false
    const url = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }))
    try { await ctx.audioWorklet.addModule(url) } finally { URL.revokeObjectURL(url) }
    return true
  } catch {
    return false
  }
}

export function prewarmAudio(): void {
  if (warm && warm.ctx.state !== 'closed') return
  const AC = typeof window !== 'undefined' ? audioContextClass() : undefined
  if (!AC) return
  try {
    const ctx = new AC({ sampleRate: TARGET_RATE, latencyHint: 'interactive' })
    warm = { ctx, worklet: loadWorklet(ctx) }
    // Created outside a user gesture it starts suspended; keep it that way.
    if (ctx.state === 'running') void ctx.suspend().catch(() => {})
  } catch {
    warm = null
  }
}
